import { useEffect, useState } from "react";
import { Search, ChevronLeft, ChevronRight, Archive } from "lucide-react";
import { format } from "date-fns";
import api from "../lib/api";
import ArticleCard from "../components/ArticleCard";
import Reveal from "../components/Reveal";
import { CATEGORIES } from "../lib/categories";

export default function ArchivePage() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [issues, setIssues] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issueData, setIssueData] = useState(null);

  useEffect(() => {
    api.get("/issues").then((r) => setIssues(r.data)).catch(() => setIssues([]));
  }, []);

  useEffect(() => {
    if (!selectedIssue) {
      setIssueData(null);
      return;
    }
    setIssueData(null);
    api.get(`/issues/${selectedIssue}`).then((r) => setIssueData(r.data)).catch(() => setIssueData({ stories: [] }));
  }, [selectedIssue]);

  useEffect(() => {
    setPage(1);
  }, [q, cat]);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = { page, limit: 12 };
      if (q.trim()) params.q = q.trim();
      if (cat !== "all") params.category = cat;
      setData(null);
      api.get("/archive", { params }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, page: 1, pages: 1 }));
    }, 250);
    return () => clearTimeout(t);
  }, [q, cat, page]);

  return (
    <div data-testid="archive-page" className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
      <Reveal>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-kinetic mb-4">The Vault</p>
        <h1 className="font-display font-black uppercase text-4xl sm:text-6xl lg:text-7xl tracking-tight">The Archive</h1>
        <p className="text-ash mt-5 max-w-xl leading-relaxed">
          Every story the desk has ever published — searched, filtered and shelved newest first.
          Yesterday's homepage heroes retire here each morning.
        </p>
      </Reveal>

      {issues && issues.length > 1 && (
        <Reveal>
          <div className="mt-10">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-ash mb-3">Back issues</p>
            <div data-testid="issue-strip" className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
              {issues.map((i) => (
                <button key={i.issue} data-testid={`issue-pill-${i.issue}`}
                  onClick={() => setSelectedIssue(selectedIssue === i.issue ? null : i.issue)}
                  className={`relative shrink-0 w-28 aspect-[3/4] overflow-hidden border transition-colors duration-300 text-left ${selectedIssue === i.issue ? "border-kinetic" : "border-white/10 hover:border-kinetic/50"}`}>
                  {i.cover?.image_url && (
                    <img src={i.cover.image_url} alt={i.cover.title} loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian/95 via-obsidian/40 to-obsidian/20" />
                  <div className="absolute inset-x-0 bottom-0 p-2.5">
                    <p className="font-display font-extrabold text-sm text-porcelain">Nº {String(i.issue).padStart(3, "0")}</p>
                    <p className="text-[9px] font-mono uppercase tracking-[0.12em] text-ash mt-0.5">{i.count} {i.count === 1 ? "story" : "stories"}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {selectedIssue ? (
        <div data-testid="issue-view" className="mt-8">
          {!issueData ? (
            <p className="font-mono text-ash text-xs uppercase tracking-[0.3em] py-24 text-center animate-pulse">Pulling the issue…</p>
          ) : (
            <>
              <Reveal>
                <div className="border border-kinetic/30 bg-white/[0.02] p-6 lg:p-8 mb-10">
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-kinetic mb-2">Back issue</p>
                  <h2 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tight">
                    Issue Nº {String(issueData.issue).padStart(3, "0")}
                  </h2>
                  <p className="text-ash font-mono text-xs uppercase tracking-[0.2em] mt-3">
                    {format(new Date(issueData.start), "d MMM")} — {format(new Date(issueData.end), "d MMM yyyy")} · {issueData.stories.length} {issueData.stories.length === 1 ? "story" : "stories"}
                  </p>
                </div>
              </Reveal>
              {issueData.stories.length === 0 ? (
                <p className="font-mono text-ash text-xs uppercase tracking-[0.3em] py-16 text-center">Nothing was filed that week.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {issueData.stories.map((a, i) => (
                    <ArticleCard key={a.id} article={a} index={i} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <>
      <Reveal>
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mt-12 mb-8">
          <div className="flex items-center gap-3 border border-white/15 focus-within:border-kinetic/60 bg-surface px-4 py-3.5 transition-colors w-full lg:max-w-md">
            <Search size={16} className="text-kinetic shrink-0" />
            <input
              data-testid="archive-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search every story we've run…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-ash/60"
            />
          </div>
          {data && (
            <p data-testid="archive-count" className="text-[11px] font-mono uppercase tracking-[0.2em] text-ash shrink-0">
              {data.total} {data.total === 1 ? "story" : "stories"} shelved
            </p>
          )}
        </div>
      </Reveal>

      <Reveal>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-10">
          <button data-testid="archive-category-all" onClick={() => setCat("all")}
            className={`whitespace-nowrap text-[11px] font-mono uppercase tracking-[0.18em] px-4 py-2 rounded-full border transition-colors duration-300 ${cat === "all" ? "bg-kinetic text-obsidian border-kinetic font-medium" : "border-white/10 text-ash hover:text-porcelain hover:border-white/25"}`}>
            All
          </button>
          {CATEGORIES.map((c) => (
            <button key={c.slug} data-testid={`archive-category-${c.slug}`} onClick={() => setCat(c.slug)}
              className={`whitespace-nowrap text-[11px] font-mono uppercase tracking-[0.18em] px-4 py-2 rounded-full border transition-colors duration-300 ${cat === c.slug ? "bg-kinetic text-obsidian border-kinetic font-medium" : "border-white/10 text-ash hover:text-porcelain hover:border-white/25"}`}>
              {c.label}
            </button>
          ))}
        </div>
      </Reveal>

      {!data ? (
        <p className="font-mono text-ash text-xs uppercase tracking-[0.3em] py-24 text-center animate-pulse">Opening the vault…</p>
      ) : data.items.length === 0 ? (
        <div className="border border-dashed border-white/15 py-24 text-center">
          <Archive size={26} className="text-ash mx-auto mb-5" />
          <p className="font-mono text-ash text-xs uppercase tracking-[0.3em]">Nothing shelved under that search.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.items.map((a, i) => (
              <ArticleCard key={a.id} article={a} index={i} />
            ))}
          </div>

          {data.pages > 1 && (
            <div data-testid="archive-pagination" className="flex items-center justify-center gap-4 mt-14">
              <button
                data-testid="archive-prev-page"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="flex items-center gap-2 border border-white/15 hover:border-kinetic/60 text-xs font-mono uppercase tracking-[0.18em] px-5 py-3 rounded-full transition-colors disabled:opacity-30 disabled:hover:border-white/15">
                <ChevronLeft size={14} /> Newer
              </button>
              <span data-testid="archive-page-indicator" className="text-xs font-mono text-ash uppercase tracking-[0.2em]">
                Page {data.page} / {data.pages}
              </span>
              <button
                data-testid="archive-next-page"
                onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                disabled={page >= data.pages}
                className="flex items-center gap-2 border border-white/15 hover:border-kinetic/60 text-xs font-mono uppercase tracking-[0.18em] px-5 py-3 rounded-full transition-colors disabled:opacity-30 disabled:hover:border-white/15">
                Older <ChevronRight size={14} />
              </button>
            </div>
          )}
            </>
          )}
        </>
      )}
    </div>
  );
}
